import React, { useContext, useMemo } from 'react'
import { Grid, Typography, Box } from '@material-ui/core'
import EasyPayPlanRadio from './EasyPayPlanRadio'
import PropTypes from 'prop-types'
import { Trans, useTranslation } from 'react-i18next'
import { isMobile } from 'react-device-detect'
import { Context } from '../../../Store'
import { BookingContext } from '../../../utils/BookingContext'
import { getCurrency, MAXIMUM_APEPAGO_LOAN_AMOUNT, PREMIUM_FEE_AMOUNT } from '../../../constants'
import { getPricing } from '../../../utils/getPricing'
import { roundToNearestTenCents } from '../../../utils/roundToNearstTenCents'
import { formatToItalianCurrency } from '../../../utils/formatToItalianCurrency'
import EasyPayPlanTooltip from './EasyPayPlanTooltip'
import { EasyPayPlanBenefitsDialog } from './EasyPayPlanBenefitDialog'

const EasyPayPlanWithOptionCardSubtitle = () => (
  <Trans
    i18nKey="easyPayPlan.bookWithEasyPayPlan.subtitle"
    components={{
      1: <strong />,
    }}
  />
)

const EasyPayPlanWithOptionCardInstallment = () => (
  <Trans
    i18nKey="easyPayPlan.bookWithEasyPayPlan.installment"
    components={{
      1: <strong />,
    }}
  />
)

function DividerLine() {
  return (
    <Box
      style={{
        borderBottom: '1px solid #000',
        width: '80%',
        opacity: '5%',
        marginBottom: '16px',
      }}
    />
  )
}

function EasyPayPlanWithOptionCard({ checked, onChange }) {
  const { t } = useTranslation()
  const [state] = useContext(Context)
  const toCurrency = (amount) => formatToItalianCurrency(roundToNearestTenCents(amount))
  const { hasCounteroffer } = useContext(BookingContext)
  const { value: propertyCurrency } = getCurrency(state.property.currency)
  const { upfrontFee, deposit } = useMemo(() => getPricing(state.property, state.booking, hasCounteroffer), [
    state.property,
    state.booking,
    hasCounteroffer,
  ])

  const useLoanAmounts = (upfrontFee, deposit) => {
    return useMemo(() => {
      const baseLoanable = Math.min(upfrontFee + deposit, MAXIMUM_APEPAGO_LOAN_AMOUNT)
      const remainingAmount = Math.max(0, upfrontFee + deposit - baseLoanable)

      const amountToPayBeforeMoveIn = toCurrency(remainingAmount)
      const totalLoanAmount = toCurrency(baseLoanable + PREMIUM_FEE_AMOUNT)
      const monthInstallmentTotalLoanAmount = toCurrency((baseLoanable + PREMIUM_FEE_AMOUNT) / 24)

      return {
        totalLoanAmount,
        amountToPayBeforeMoveIn,
        monthInstallmentTotalLoanAmount,
      }
    }, [upfrontFee, deposit])
  }

  const { totalLoanAmount, amountToPayBeforeMoveIn, monthInstallmentTotalLoanAmount } = useLoanAmounts(upfrontFee, deposit)

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
        borderRadius: 12,
      }}
      onClick={onChange}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#DCF2F4',
          padding: '12px 0px',
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
        }}
      >
        <EasyPayPlanRadio checked={checked} />
        <Typography
          style={{
            fontWeight: 700,
            fontSize: 16,
            color: '#0C9D9D',
            marginLeft: 6,
            marginBottom: 0,
            textAlign: 'center',
          }}
        >
          {t('easyPayPlan.bookWithEasyPayPlan.title')}
        </Typography>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px 8px', flexGrow: 1 }}>
        <Typography
          style={{ fontSize: isMobile ? 15 : 16, fontWeight: 400, color: '#394555', marginBottom: 8, textAlign: 'center' }}
        >
          <EasyPayPlanWithOptionCardSubtitle />
        </Typography>
        <Typography style={{ fontSize: 24, fontWeight: 700, color: '#0C9D9D', marginBottom: 8, textAlign: 'center' }}>
          {amountToPayBeforeMoveIn}
          {propertyCurrency}
          <EasyPayPlanTooltip />
        </Typography>
        <DividerLine />
        <Typography
          style={{ fontSize: isMobile ? 13 : 16, fontWeight: 400, color: '#394555', marginBottom: 16, textAlign: 'center' }}
        >
          <EasyPayPlanWithOptionCardInstallment />
        </Typography>
        <Typography
          style={{ fontSize: isMobile ? 16 : 20, fontWeight: 700, color: '#0C9D9D', marginBottom: 4, textAlign: 'center' }}
        >
          {monthInstallmentTotalLoanAmount}
        </Typography>
        <Typography style={{ fontSize: 11, fontWeight: 400, color: '#394555', marginBottom: 4, textAlign: 'center' }}>
          <Trans
            i18nKey="easyPayPlan.bookWithEasyPayPlan.disclaimer"
            values={{
              premium_fee: toCurrency(PREMIUM_FEE_AMOUNT),
              currency: propertyCurrency,
            }}
          />
        </Typography>
        <EasyPayPlanBenefitsDialog />
      </div>
    </div>
  )
}

EasyPayPlanWithOptionCard.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
}

export default EasyPayPlanWithOptionCard
